import React, { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { API, useAuth } from "../App";
import { setSessionToken } from "../lib/sessionToken";

// Module-scope guard — survives React StrictMode remounts (a useRef would
// not, because StrictMode unmounts and re-mounts in dev/preview, giving
// the ref a fresh value on the second mount and firing a duplicate POST).
// Each session_id from Emergent is single-use, so a duplicate POST 404s at
// the upstream `/auth/v1/env/oauth/session-data` endpoint and shows the
// user a spurious "Authentication failed" toast.
const _processedSessionIds = new Set();

const AuthCallback = () => {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const hasProcessed = useRef(false);

  useEffect(() => {
    // Belt-and-braces: local ref catches the fast within-mount re-run,
    // module-scope Set catches the StrictMode remount case.
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      const hash = window.location.hash;
      const sessionIdMatch = hash.match(/session_id=([^&]+)/);

      if (!sessionIdMatch) {
        toast.error("Invalid authentication response");
        navigate("/", { replace: true });
        return;
      }

      const sessionId = sessionIdMatch[1];

      // Global de-dupe on the session_id itself. If StrictMode re-mounts
      // this component (dev/preview only), the second mount hits this
      // guard and returns before double-consuming the session.
      if (_processedSessionIds.has(sessionId)) {
        return;
      }
      _processedSessionIds.add(sessionId);

      try {
        const response = await axios.post(
          `${API}/auth/session`,
          { session_id: sessionId }
        );

        // Keep the token for this tab only (memory, never localStorage).
        if (response.data?.session_token) {
          setSessionToken(response.data.session_token);
        }

        setUser(response.data);
        toast.success("Welcome back!");

        // Clear the hash and navigate to dashboard
        window.history.replaceState(null, "", window.location.pathname);
        navigate("/dashboard", { replace: true, state: { user: response.data } });
      } catch (error) {
        console.error("Auth callback error:", error);
        toast.error("Authentication failed. Please try again.");
        // Allow retry with a fresh login — release the one-shot guard so
        // the user can hit "Continue with Google" again cleanly.
        _processedSessionIds.delete(sessionId);
        navigate("/", { replace: true });
      }
    };

    processAuth();
  }, [navigate, setUser]);

  return (
    <div className="min-h-screen bg-saturn-black flex items-center justify-center">
      <div className="text-center">
        <div className="w-16 h-16 rounded-full bg-saturn-orange/20 flex items-center justify-center mx-auto mb-4 animate-pulse">
          <span className="font-orbitron font-bold text-saturn-orange text-2xl">S</span>
        </div>
        <p className="text-slate-400 font-rajdhani">Authenticating...</p>
      </div>
    </div>
  );
};

export default AuthCallback;
